import { useEffect, useMemo, useState } from 'react';
import type { Appointment, Staff, WorkingHours } from '@salonomy/types';
import { Button, Pill, Select, TextInput } from '@salonomy/ui';
import {
  useSetWorkingHours,
  useUpcomingAppointments,
  useWorkingHours,
} from '../../data/working-hours.js';
import {
  addDays,
  clockLabel,
  isoDateInZone,
  spanLabel,
  todayInZone,
  wallMinutes,
  weekdayInZone,
} from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS6 brief item 3 — the ROTA BASE GRID: a per-staff WEEKLY working-hours editor over
 * PUT /staff/:id/working-hours. This is the recurring weekly band that drives the availability
 * engine; the WS7 RotaEditor (time off + per-date hour exceptions) layers date-specific overrides on
 * top. Integrate composes this grid INTO RotaEditor alongside that editor.
 *
 * Visual day editor with copy-day / copy-week / clear-day. CRITICAL (brief): a change must show its
 * effect against existing bookings — we read the staff member's upcoming appointments and WARN when a
 * proposed band would leave a booking outside the new hours ("Tuesday now closes at 15:00 — 2 existing
 * bookings fall outside these hours"), never silently stranding them.
 *
 * RBAC mirrors the API as in RotaEditor: a manager edits anyone; a chair_renter edits only their own
 * base grid (the caller fixes `staff` to `[me]`). Caller already filtered to chair-holding roles.
 */

// Display order Mon→Sun for a UK salon week; weekday codes stay 0=Sun … 6=Sat (matches WorkingHours).
const WEEK: Array<{ weekday: number; short: string; long: string }> = [
  { weekday: 1, short: 'Mon', long: 'Monday' },
  { weekday: 2, short: 'Tue', long: 'Tuesday' },
  { weekday: 3, short: 'Wed', long: 'Wednesday' },
  { weekday: 4, short: 'Thu', long: 'Thursday' },
  { weekday: 5, short: 'Fri', long: 'Friday' },
  { weekday: 6, short: 'Sat', long: 'Saturday' },
  { weekday: 0, short: 'Sun', long: 'Sunday' },
];

/** One working block in the editor; times are wall-clock "HH:MM" in the salon zone. */
interface Block {
  start: string;
  end: string;
}
/** weekday (0–6) → ordered list of blocks. An empty list means closed that day. */
type Grid = Record<number, Block[]>;

const DEFAULT_BLOCK: Block = { start: '09:00', end: '17:00' };

function emptyGrid(): Grid {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

/** Build editor state from the API's flat WorkingHours rows, ordered by start time within each day. */
function gridFromHours(hours: WorkingHours[]): Grid {
  const g = emptyGrid();
  for (const h of hours) {
    (g[h.weekday] ??= []).push({ start: h.startTime, end: h.endTime });
  }
  for (const wd of Object.keys(g)) g[+wd]!.sort((a, b) => a.start.localeCompare(b.start));
  return g;
}

/** Flatten editor state back to the PUT body (skips zero-length / inverted blocks defensively). */
function hoursFromGrid(g: Grid): Array<{ weekday: number; startTime: string; endTime: string }> {
  const out: Array<{ weekday: number; startTime: string; endTime: string }> = [];
  for (const { weekday } of WEEK) {
    for (const b of g[weekday] ?? []) {
      if (b.start && b.end && b.end > b.start) {
        out.push({ weekday, startTime: b.start, endTime: b.end });
      }
    }
  }
  return out;
}

function blocksEqual(a: Block[], b: Block[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => x.start === b[i]!.start && x.end === b[i]!.end);
}
function gridEqual(a: Grid, b: Grid): boolean {
  return WEEK.every(({ weekday }) => blocksEqual(a[weekday] ?? [], b[weekday] ?? []));
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
function toMin(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

/** A booking that would fall outside the PROPOSED hours for its weekday — the stranding warning. */
interface Stranded {
  appt: Appointment;
  weekday: number;
  /** Plain-English reason, e.g. "closes at 3:00" or "this day is now off". */
  reason: string;
}

/**
 * Compare upcoming bookings against the proposed grid. A booking is "stranded" when any part of it
 * falls outside every working block on its weekday (or the day is closed). Mirrors the availability
 * engine's containment notion: a block covers a booking only if it wraps the whole [start, end).
 */
function findStranded(appts: Appointment[], grid: Grid, tz: string): Stranded[] {
  const out: Stranded[] = [];
  for (const a of appts) {
    // weekday from the booking's own salon-zone date so we honour the zone (and DST) exactly.
    const date = isoDateInZone(new Date(a.startAt), tz);
    const weekday = weekdayInZone(date, tz);
    const blocks = grid[weekday] ?? [];
    const startMin = wallMinutes(a.startAt, tz);
    const endMin = wallMinutes(a.endAt, tz);

    if (blocks.length === 0) {
      out.push({ appt: a, weekday, reason: 'this day is now off' });
      continue;
    }
    const covered = blocks.some(
      (b) =>
        HHMM.test(b.start) &&
        HHMM.test(b.end) &&
        toMin(b.start) <= startMin &&
        toMin(b.end) >= endMin,
    );
    if (!covered) {
      const latest = Math.max(...blocks.map((b) => toMin(b.end)));
      const earliest = Math.min(...blocks.map((b) => toMin(b.start)));
      const reason =
        endMin > latest
          ? `closes at ${clockLabel(latest)}`
          : startMin < earliest
            ? `opens at ${clockLabel(earliest)}`
            : 'now falls in a break';
      out.push({ appt: a, weekday, reason });
    }
  }
  return out;
}

export function WorkingHoursGrid({
  staff,
  tz,
}: {
  /** The staff member whose weekly base grid we edit. The caller decides who this is per RBAC. */
  staff: Staff;
  tz: string;
}) {
  const toast = useToast();
  const { data: hours, isLoading } = useWorkingHours(staff.id);
  const save = useSetWorkingHours(staff.id);

  // The persisted grid (server truth) and the working draft. Re-seed when the staff member changes or
  // the server rows arrive/refresh.
  const persisted = useMemo<Grid>(() => gridFromHours(hours ?? []), [hours]);
  const [grid, setGrid] = useState<Grid>(persisted);
  useEffect(() => setGrid(persisted), [persisted]);

  const dirty = !gridEqual(grid, persisted);

  // Conflict scan: upcoming bookings for THIS member over the next 8 weeks (one request). We only
  // surface stranded bookings while there's an unsaved change — and only against the persisted hours'
  // window so we don't second-guess a band the operator hasn't touched.
  const today = todayInZone(tz);
  const fromIso = useMemo(() => new Date(`${today}T00:00:00Z`).toISOString(), [today]);
  const toIso = useMemo(() => new Date(`${addDays(today, 56)}T00:00:00Z`).toISOString(), [today]);
  const { data: upcoming = [] } = useUpcomingAppointments(staff.id, fromIso, toIso);

  const stranded = useMemo(
    () => (dirty ? findStranded(upcoming, grid, tz) : []),
    [dirty, upcoming, grid, tz],
  );
  const strandedByDay = useMemo(() => {
    const m = new Map<number, Stranded[]>();
    for (const s of stranded) {
      const list = m.get(s.weekday);
      if (list) list.push(s);
      else m.set(s.weekday, [s]);
    }
    return m;
  }, [stranded]);

  // ── Mutators (immutable) ──
  function setDay(weekday: number, blocks: Block[]) {
    setGrid((g) => ({ ...g, [weekday]: blocks }));
  }
  function toggleOpen(weekday: number, open: boolean) {
    setDay(weekday, open ? [{ ...DEFAULT_BLOCK }] : []);
  }
  function setBlock(weekday: number, index: number, patch: Partial<Block>) {
    setDay(
      weekday,
      (grid[weekday] ?? []).map((b, i) => (i === index ? { ...b, ...patch } : b)),
    );
  }
  function addBlock(weekday: number) {
    const existing = grid[weekday] ?? [];
    // Default a split to "after lunch" so the common 2-block day needs minimal typing.
    const next: Block = existing.length ? { start: '14:00', end: '17:00' } : { ...DEFAULT_BLOCK };
    setDay(weekday, [...existing, next]);
  }
  function removeBlock(weekday: number, index: number) {
    setDay(
      weekday,
      (grid[weekday] ?? []).filter((_, i) => i !== index),
    );
  }
  function clearDay(weekday: number) {
    setDay(weekday, []);
  }
  function copyDayToAll(weekday: number) {
    const src = (grid[weekday] ?? []).map((b) => ({ ...b }));
    setGrid((g) => {
      const next: Grid = { ...g };
      for (const { weekday: wd } of WEEK) next[wd] = src.map((b) => ({ ...b }));
      return next;
    });
    toast.success('Copied to every day');
  }
  function copyDayToWeekdays(weekday: number) {
    const src = (grid[weekday] ?? []).map((b) => ({ ...b }));
    setGrid((g) => {
      const next: Grid = { ...g };
      for (const wd of [1, 2, 3, 4, 5]) next[wd] = src.map((b) => ({ ...b }));
      return next;
    });
    toast.success('Copied to Mon–Fri');
  }
  function resetDraft() {
    setGrid(persisted);
  }

  async function submit() {
    // Validate every block before we touch the network.
    for (const { weekday, long } of WEEK) {
      for (const b of grid[weekday] ?? []) {
        if (!HHMM.test(b.start) || !HHMM.test(b.end)) {
          toast.error(`${long}: enter valid start and end times.`);
          return;
        }
        if (b.end <= b.start) {
          toast.error(`${long}: end time must be after the start time.`);
          return;
        }
      }
      const blocks = [...(grid[weekday] ?? [])].sort((a, b) => a.start.localeCompare(b.start));
      for (let i = 1; i < blocks.length; i++) {
        if (blocks[i]!.start < blocks[i - 1]!.end) {
          toast.error(`${long}: working blocks can’t overlap.`);
          return;
        }
      }
    }
    try {
      await save.mutateAsync({ hours: hoursFromGrid(grid) });
      toast.success(`Working hours saved for ${staff.displayName}`);
    } catch {
      toast.error('Could not save these working hours.');
    }
  }

  if (isLoading) {
    return (
      <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
        Loading working hours…
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div className="rota-subhead">
        <div className="rota-subhead__title">Weekly working hours</div>
        {dirty ? (
          <Pill tone="cobalt" className="mono">
            Unsaved
          </Pill>
        ) : null}
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 4 }}>
        The base week that drives availability. Time off and one-off changes layer on top below.
      </div>

      <div className="settings-list">
        {WEEK.map(({ weekday, short, long }) => {
          const blocks = grid[weekday] ?? [];
          const open = blocks.length > 0;
          const dayStranded = strandedByDay.get(weekday) ?? [];
          return (
            <div
              key={weekday}
              className="settings-row"
              style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 96 }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={open}
                  aria-label={`${long} ${open ? 'open' : 'closed'}`}
                  onClick={() => toggleOpen(weekday, !open)}
                  className={`rota-staffbar__btn${open ? ' rota-staffbar__btn--active' : ''}`}
                  style={{ width: 64, textAlign: 'center' }}
                >
                  {short}
                </button>
              </div>

              <div style={{ flex: 1, minWidth: 220 }}>
                {open ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {blocks.map((b, i) => (
                      <div
                        key={i}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                      >
                        <TextInput
                          type="time"
                          value={b.start}
                          aria-label={`${long} start`}
                          onChange={(e) => setBlock(weekday, i, { start: e.target.value })}
                          style={{ width: 116 }}
                        />
                        <span className="muted" style={{ fontSize: 13 }}>
                          to
                        </span>
                        <TextInput
                          type="time"
                          value={b.end}
                          aria-label={`${long} end`}
                          onChange={(e) => setBlock(weekday, i, { end: e.target.value })}
                          style={{ width: 116 }}
                        />
                        {blocks.length > 1 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeBlock(weekday, i)}
                            aria-label={`Remove block from ${long}`}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button size="sm" variant="ghost" onClick={() => addBlock(weekday)}>
                        Split / add hours
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="muted" style={{ fontSize: 13 }}>
                    Closed
                  </span>
                )}

                {dayStranded.length > 0 ? (
                  <div className="conflict-list" style={{ marginTop: 10 }}>
                    <div className="conflict-list__head">
                      {long} now {strandReason(dayStranded)} — {dayStranded.length} existing booking
                      {dayStranded.length === 1 ? '' : 's'} fall outside these hours.
                    </div>
                    {dayStranded.slice(0, 4).map(({ appt: a }) => (
                      <div key={a.id} className="conflict-list__row">
                        <span style={{ fontWeight: 600 }}>{a.clientDisplayName || 'Walk-in'}</span>
                        <span className="muted" style={{ fontSize: 12 }}>
                          {isoDateInZone(new Date(a.startAt), tz)} ·{' '}
                          {spanLabel(a.startAt, a.endAt, tz)}
                        </span>
                      </div>
                    ))}
                    {dayStranded.length > 4 ? (
                      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                        +{dayStranded.length - 4} more.
                      </div>
                    ) : null}
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Saving won’t cancel these — rebook or notify the client first.
                    </div>
                  </div>
                ) : null}
              </div>

              <DayActions
                hasHours={open}
                onCopyWeekdays={() => copyDayToWeekdays(weekday)}
                onCopyAll={() => copyDayToAll(weekday)}
                onClear={() => clearDay(weekday)}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 14,
          flexWrap: 'wrap',
        }}
      >
        <div className="muted" style={{ fontSize: 12.5 }}>
          {stranded.length > 0
            ? `${stranded.length} existing booking${stranded.length === 1 ? '' : 's'} fall outside the new hours.`
            : dirty
              ? 'No existing bookings clash with these hours.'
              : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {dirty ? (
            <Button variant="outline" size="sm" onClick={resetDraft} disabled={save.isPending}>
              Discard
            </Button>
          ) : null}
          <Button onClick={submit} disabled={!dirty || save.isPending}>
            Save working hours
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The most-severe phrase across a day's stranded bookings, for the warning headline. */
function strandReason(items: Stranded[]): string {
  if (items.some((s) => s.reason === 'this day is now off')) return 'closed';
  const closes = items.find((s) => s.reason.startsWith('closes'));
  if (closes) return closes.reason;
  const opens = items.find((s) => s.reason.startsWith('opens'));
  if (opens) return opens.reason;
  return 'has changed hours';
}

/** Per-day copy / clear menu. A tiny inline Select avoids a new dependency on a popover primitive. */
function DayActions({
  hasHours,
  onCopyWeekdays,
  onCopyAll,
  onClear,
}: {
  hasHours: boolean;
  onCopyWeekdays: () => void;
  onCopyAll: () => void;
  onClear: () => void;
}) {
  const [value, setValue] = useState('');
  function run(v: string) {
    if (v === 'weekdays') onCopyWeekdays();
    else if (v === 'all') onCopyAll();
    else if (v === 'clear') onClear();
    setValue('');
  }
  return (
    <Select
      value={value}
      aria-label="Day actions"
      onChange={(e) => run(e.target.value)}
      style={{ width: 150, flexShrink: 0 }}
    >
      <option value="">Copy / clear…</option>
      <option value="weekdays" disabled={!hasHours}>
        Copy to Mon–Fri
      </option>
      <option value="all" disabled={!hasHours}>
        Copy to every day
      </option>
      <option value="clear">Clear this day</option>
    </Select>
  );
}
